import { GoogleOAuthProvider } from '@react-oauth/google'
import { FullPageSpinner } from 'components'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useRedirectUrl, useCliParamsCapture, useCliAuthCallback } from './hooks'
import { LoginForm } from 'modules/authentication'
import { selectIsLoggedIn } from 'modules/common'
import { CliAuthError } from 'modules/onboarding/components/CliAuthError'
import { selectOnboardingCompleted } from 'modules/onboarding/slices'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { initOnboardingState, selectOnboardingUrlStateInitialized } from 'modules/onboarding/slices'

type CliSendState = 'idle' | 'sending' | 'success' | 'failed'

export const LoginLayout = () => {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const onboardingCompleted = useAppSelector(selectOnboardingCompleted)
  const onboardingStateInitialized = useAppSelector(selectOnboardingUrlStateInitialized)
  const { redirectPath, redirectUrlSearchParam } = useRedirectUrl()

  useCliParamsCapture()
  const { isCliMode, sendTokensToCli } = useCliAuthCallback()
  const [cliSendState, setCliSendState] = useState<CliSendState>('idle')

  useEffect(() => {
    dispatch(initOnboardingState())
  }, [dispatch])

  useEffect(() => {
    if (isLoggedIn && onboardingCompleted && isCliMode && cliSendState === 'idle') {
      setCliSendState('sending')
      sendTokensToCli().then(success => {
        setCliSendState(success ? 'success' : 'failed')
      })
    }
  }, [isLoggedIn, onboardingCompleted, isCliMode, cliSendState, sendTokensToCli])

  if (isLoggedIn) {
    if (!onboardingStateInitialized) {
      return <FullPageSpinner />
    }

    // CLI mode + already onboarded: wait for token send, then redirect or show error
    if (isCliMode && onboardingCompleted) {
      if (cliSendState === 'failed') {
        return <CliAuthError />
      }
      return cliSendState === 'success' ? (
        <Navigate to="/cli-signup-success" replace />
      ) : (
        <FullPageSpinner />
      )
    }

    if (redirectPath) {
      return <Navigate to={redirectPath} replace />
    }
    if (redirectUrlSearchParam) {
      return <Navigate to={redirectUrlSearchParam} replace />
    }
    return <Navigate to="/" replace />
  }

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}>
      <LoginForm />
    </GoogleOAuthProvider>
  )
}
